import React, { Component } from 'react';
import { DataProvider } from './DataProvider';
import './index.css';

const DEFAULT_WINDOW_SIZE = 11;
const noop = () => {};

function toPositiveInteger(value, fallback = DEFAULT_WINDOW_SIZE) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.ceil(number) : fallback;
}

function escapeAttributeValue(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function defaultGetId(item) {
  return item && item.id;
}

function defaultGetSelector(id) {
  return `[data-id="${escapeAttributeValue(id)}"]`;
}

function normalizeLoadResult(result) {
  if (!result || typeof result !== 'object') {
    return { count: 0, update: noop };
  }

  return {
    count: Math.max(0, Number(result.count) || 0),
    update: typeof result.update === 'function' ? result.update : noop,
  };
}

export class InfiniteScroll extends Component {
  dataProvider = null;

  componentDidMount() {
    if (this.props.onCreate) {
      this.props.onCreate(this);
    }
  }

  setDataProvider = dataProvider => {
    this.dataProvider = dataProvider;
  };

  getContentRef = () => {
    return this.dataProvider ? this.dataProvider.getContentRef() : null;
  };

  isBottomLocked = () => {
    return Boolean(
      this.dataProvider &&
      this.dataProvider.isBottomLocked &&
      this.dataProvider.isBottomLocked()
    );
  };

  onLoadMore = async direction => {
    if (!this.props.onLoadMore) {
      return { count: 0, update: noop };
    }

    const pageSize = toPositiveInteger(this.props.pageSize);
    const result = await this.props.onLoadMore(direction, pageSize);
    return normalizeLoadResult(result);
  };

  onUnload = async (direction, count) => {
    if (!this.props.onUnload) {
      return { count: 0, update: noop };
    }

    const result = await this.props.onUnload(direction, count);
    return normalizeLoadResult(result);
  };

  loadMore = async direction => {
    if (!this.dataProvider) {
      return { count: 0, update: noop };
    }
    return await this.dataProvider.handleLoadMore(direction);
  };

  scrollToItem = async (item, options, then) => {
    if (!this.dataProvider) return false;
    return this.dataProvider.scrollToItem(item, options, then);
  };

  scrollToTop = async () => {
    if (!this.dataProvider) return false;
    return this.dataProvider.scrollToTop();
  };

  scrollToBottom = async () => {
    if (!this.dataProvider) return false;
    return this.dataProvider.scrollToBottom();
  };

  scrollToTopItem = async () => {
    if (!this.dataProvider) return false;
    return this.dataProvider.scrollToTopItem();
  };

  scrollToBottomItem = async () => {
    if (!this.dataProvider) return false;
    return this.dataProvider.scrollToBottomItem();
  };

  setScrollPos = async ({ baseOffset = 0, offset = 0, scrollTop = 0 } = {}) => {
    if (!this.dataProvider) return false;

    return this.dataProvider.setScrollPos({
      baseOffset,
      offset,
      scrollTop,
    });
  };

  setScrollTop = scrollTop => {
    if (!this.dataProvider || !this.dataProvider.setScrollTop) return false;
    return this.dataProvider.setScrollTop(scrollTop);
  };

  render() {
    const windowSize = toPositiveInteger(
      this.props.windowSize || this.props.pageSize,
      DEFAULT_WINDOW_SIZE,
    );

    return (
      <DataProvider
        onScroll={this.props.onScroll}
        onBottomLockChanged={this.props.onBottomLockChanged}
        isBottomAligned={this.props.isBottomAligned}
        onKeyDown={this.props.onKeyDown}
        autoFocus={this.props.autoFocus}
        onCreate={this.setDataProvider}
        onUnload={this.onUnload}
        onLoadMore={this.onLoadMore}
        windowSize={windowSize}
        overscan={this.props.overscan}
        edgeLookaheadScreens={this.props.edgeLookaheadScreens}
        maxEdgeLookaheadScreens={this.props.maxEdgeLookaheadScreens}
        refreshSafariCompositorOnWindowSwap={this.props.refreshSafariCompositorOnWindowSwap}
        items={Array.isArray(this.props.items) ? this.props.items : []}
        getId={this.props.getId || defaultGetId}
        getSelector={this.props.getSelector || defaultGetSelector}
        renderItems={this.props.renderItems}
        debug={Boolean(this.props.debug)}
      />
    );
  }
}
